import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {AuthCheck} from '../../helpers'
import {AuthorizedApiBase} from '../../api/api-client-config'
import {
  FetchInvoicesClient,
  ImportInvoicesClient,
  GetImportSessionClient,
} from '../../api'

const SERVICE_URL = window.ENVVARS.REACT_APP_SERVICE_URL

class DigitalPlanetListSessionsClient extends AuthorizedApiBase {
  constructor(baseUrl) {
    super()
    this.baseUrl = baseUrl
    this.http = window
  }
  list() {
    const url = `${this.baseUrl}/api/v1/digitalplanet/sessions`
    const options = {method: 'GET', headers: {Accept: 'application/json'}}
    return this.transformOptions(options)
      .then((opts) => this.http.fetch(url, opts))
      .then((response) => {
        if (response.status === 200) return response.json()
        return response.text().then((text) => {
          throw new Error(text || 'Aktarım geçmişi alınamadı')
        })
      })
  }
}

class DigitalPlanetInvoicePdfClient extends AuthorizedApiBase {
  constructor(baseUrl) {
    super()
    this.baseUrl = baseUrl
    this.http = window
  }
  getPdf(uuid) {
    const url = `${this.baseUrl}/api/v1/digitalplanet/pdf/${encodeURIComponent(uuid)}`
    const options = {method: 'GET', headers: {Accept: 'application/octet-stream'}}
    return this.transformOptions(options)
      .then((opts) => this.http.fetch(url, opts))
      .then((response) => {
        if (response.status === 200 || response.status === 206) {
          const cd = response.headers?.get('content-disposition')
          const m = cd ? /filename="?([^"]*?)"?(;|$)/g.exec(cd) : null
          const fileName = m?.[1]
          return response.blob().then((blob) => ({fileName, data: blob, status: response.status}))
        }
        return response.text().then((text) => {
          throw new Error(text || 'PDF alınamadı')
        })
      })
  }
}

const listSessionsClient = new DigitalPlanetListSessionsClient(SERVICE_URL)
const fetchInvoicesClient = new FetchInvoicesClient(SERVICE_URL)
const importInvoicesClient = new ImportInvoicesClient(SERVICE_URL)
const getImportSessionClient = new GetImportSessionClient(SERVICE_URL)
const getInvoicePdfClient = new DigitalPlanetInvoicePdfClient(SERVICE_URL)

const initialState = {
  loading: false,
  fetchLoading: false,
  importLoading: false,
  sessionDetailLoading: false,
  error: null,
  invoiceList: null,
  importResult: null,
  sessionList: null,
  sessionDetail: null,
  pdfData: null,
}

export const digitalPlanetListSessions = createAsyncThunk(
  'digitalplanetinvoice/listSessions',
  async (_, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await listSessionsClient.list()
      dispatch(setSessionList(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error?.message || 'Aktarım geçmişi alınırken hata oluştu'))
      throw error
    }
  }
)

export const digitalPlanetFetchInvoices = createAsyncThunk(
  'digitalplanetinvoice/fetch',
  async (params, {dispatch}) => {
    try {
      dispatch(setFetchLoading(true))
      const toDateOnly = (s) => ({toISOString: () => s})
      const response = await fetchInvoicesClient.fetch(toDateOnly(params.startDate), toDateOnly(params.endDate))
      dispatch(setInvoiceList(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error?.message || 'Fatura listesi alınırken hata oluştu'))
      throw error
    }
  }
)

export const digitalPlanetImportInvoices = createAsyncThunk(
  'digitalplanetinvoice/import',
  async (body, {dispatch}) => {
    try {
      dispatch(setImportLoading(true))
      const response = await importInvoicesClient.import(body)
      dispatch(setImportResult(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error?.message || 'Aktarım sırasında hata oluştu'))
      throw error
    }
  }
)

export const digitalPlanetGetSession = createAsyncThunk(
  'digitalplanetinvoice/getSession',
  async (id, {dispatch}) => {
    try {
      dispatch(setSessionDetailLoading(true))
      const response = await getImportSessionClient.get(id)
      dispatch(setSessionDetail(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setSessionDetailLoading(false))
      dispatch(setError(error?.message || 'Oturum detayı alınırken hata oluştu'))
      throw error
    }
  }
)

export const digitalPlanetGetPdf = createAsyncThunk(
  'digitalplanetinvoice/getPdf',
  async (uuid, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await getInvoicePdfClient.getPdf(uuid)
      dispatch(setPdfData(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error?.message || 'PDF alınırken hata oluştu'))
      throw error
    }
  }
)

const digitalplanetinvoiceSlice = createSlice({
  name: 'digitalplanetinvoice',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setFetchLoading: (state, action) => {
      state.fetchLoading = action.payload
    },
    setImportLoading: (state, action) => {
      state.importLoading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
      state.loading = false
      state.fetchLoading = false
      state.importLoading = false
    },
    setInvoiceList: (state, action) => {
      state.invoiceList = action.payload
      state.fetchLoading = false
    },
    setImportResult: (state, action) => {
      state.importResult = action.payload
      state.importLoading = false
    },
    setSessionList: (state, action) => {
      state.sessionList = action.payload
      state.loading = false
    },
    setSessionDetailLoading: (state, action) => {
      state.sessionDetailLoading = action.payload
    },
    setSessionDetail: (state, action) => {
      state.sessionDetail = action.payload
      state.sessionDetailLoading = false
    },
    setPdfData: (state, action) => {
      state.pdfData = action.payload
      state.loading = false
    },
    clearError: (state) => {
      state.error = null
    },
    resetState: (state) => {
      Object.assign(state, initialState)
    },
  },
})

export const {
  setLoading,
  setFetchLoading,
  setImportLoading,
  setSessionDetailLoading,
  setError,
  setInvoiceList,
  setImportResult,
  setSessionList,
  setSessionDetail,
  setPdfData,
  clearError,
  resetState,
} = digitalplanetinvoiceSlice.actions

export const selectDigitalPlanetInvoiceList = (state) => state.digitalplanetinvoice.invoiceList
export const selectDigitalPlanetImportResult = (state) => state.digitalplanetinvoice.importResult
export const selectDigitalPlanetFetchLoading = (state) => state.digitalplanetinvoice.fetchLoading
export const selectDigitalPlanetImportLoading = (state) => state.digitalplanetinvoice.importLoading
export const selectDigitalPlanetError = (state) => state.digitalplanetinvoice.error
export const selectDigitalPlanetSessionList = (state) => state.digitalplanetinvoice.sessionList
export const selectDigitalPlanetSessionDetail = (state) => state.digitalplanetinvoice.sessionDetail
export const selectDigitalPlanetSessionDetailLoading = (state) => state.digitalplanetinvoice.sessionDetailLoading
export const selectDigitalPlanetPdfData = (state) => state.digitalplanetinvoice.pdfData
export const selectDigitalPlanetLoading = (state) => state.digitalplanetinvoice.loading

export default digitalplanetinvoiceSlice.reducer
